import React, { useState } from "react";
import Text from "components/common/Text";
import Button from "components/common/Button";
import Link from "components/common/Link";
import Stack from "components/common/Stack";
import OtpInput from "react-otp-input";
import { config } from "config";
import AuthTextField from "components/Auth/shared/AuthTextField";
import AuthPhoneField from "components/Auth/shared/AuthPhoneField";
import AuthPrimaryButton from "components/Auth/shared/AuthPrimaryButton";
import AuthCheckbox from "components/Auth/shared/AuthCheckbox";
import { useAuthFieldStyles } from "components/Auth/shared/authFieldStyles";

const SignUpForm = ({
  onRequestOtp,
  onSubmit,
  onChange,
  onResendOtp,
  onBackToStep1,
  formData,
  formManage,
  mobileResendTime,
  isLoading,
  formError,
  loginUrl,
  product,
}) => {
  const classes = useAuthFieldStyles();
  const [hasAgreedConcent, setHasAgreedConcent] = useState(true);
  const [hasAgreedTnc, setHasAgreedTnc] = useState(false);

  const handleAgreementClick = (type, e) => {
    if (type === "consent") {
      setHasAgreedConcent(e.target.checked);
    } else if (type === "tnc") {
      setHasAgreedTnc(e.target.checked);
    }
  };

  const disabledToProceed = () => !(hasAgreedTnc && hasAgreedConcent);

  const linkSx = {
    color: "#2977ec",
    textDecoration: "underline",
    fontSize: "12px",
    fontFamily: "'DM Sans', sans-serif",
  };

  const agreementTextSx = {
    fontSize: "12px",
    color: "#000",
    lineHeight: 1.3,
    fontFamily: "'DM Sans', sans-serif",
  };

  return (
    <Stack
      alignItems="center"
      sx={{
        py: { xs: 3, md: 2 },
        px: { xs: 3, md: 0 },
        width: "100%",
        minHeight: "100%",
        position: "relative",
        boxSizing: "border-box",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <Stack
        sx={{
          width: { xs: "100%", md: 546 },
          maxWidth: "100%",
          gap: { xs: "24px", md: 0 },
        }}
        alignItems="stretch"
        justifyContent="flex-start"
      >
        {formManage.isRequestOtp ? (
          <Text
            sx={{
              fontSize: "22px",
              fontWeight: 500,
              mb: { xs: 0, md: 3 },
              mt: { xs: 1, md: 2 },
            }}
          >
            OTP Verification
          </Text>
        ) : null}

        {!formManage.isRequestOtp ? (
          <Stack sx={{ width: 1, gap: 0 }}>
            <AuthTextField
              label="Name"
              placeholder="Enter your name"
              value={formData.name}
              onChange={(e) => onChange("name", e.target.value)}
              error={formError.name}
            />
            <AuthTextField
              label="Email ID"
              type="email"
              placeholder="you@gmail.com"
              value={formData.email}
              onChange={(e) => onChange("email", e.target.value)}
              error={formError.email}
            />
            <AuthPhoneField
              value={formData.mobile}
              onChange={(digits) => onChange("mobile", digits)}
              error={formError.mobile}
              sx={{ mb: 0 }}
            />
          </Stack>
        ) : null}

        {formManage.isRequestOtp ? (
          <Stack sx={{ width: 1, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between">
              <Text
                variant="caption1"
                sx={{ wordBreak: "break-word", marginRight: "10px" }}
              >
                Enter OTP sent on {formData.mobile}
              </Text>
              <Button
                component="span"
                className={classes.otpResend}
                sx={{ fontSize: "15px" }}
                onClick={onBackToStep1}
                disabled={mobileResendTime > 0}
                loading={isLoading}
              >
                Edit
              </Button>
            </Stack>
            <OtpInput
              className={classes.otpContainer}
              inputStyle={classes.otpInput}
              value={formData.mobileOTP}
              numInputs={6}
              isInputNum={true}
              shouldAutoFocus={true}
              onChange={(e) => onChange("mobileOTP", e)}
              separator={<span>&nbsp;</span>}
            />
            <Stack
              sx={{
                whiteSpace: "nowrap",
                alignSelf: "flex-end",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                flexFlow: "row wrap",
                fontSize: "15px",
              }}
            >
              <Button
                component="span"
                className={classes.otpResend}
                sx={{ fontSize: "15px" }}
                onClick={() => onResendOtp("mobile")}
                disabled={mobileResendTime > 0}
                loading={isLoading}
              >
                Resend {mobileResendTime <= 0 && "OTP"}
              </Button>
              {mobileResendTime > 0 ? (
                <Text variant="caption1" sx={{ wordBreak: "break-word" }}>
                  in 00:
                  {mobileResendTime < 10
                    ? `0${mobileResendTime}`
                    : mobileResendTime}
                </Text>
              ) : null}
            </Stack>
          </Stack>
        ) : null}

        {formManage.isRequestOtp ? (
          <AuthPrimaryButton
            onClick={() => onSubmit(product)}
            loading={isLoading}
            disabled={formData.mobileOTP.length !== 6}
            sx={{ mb: 4 }}
          >
            Confirm OTP
          </AuthPrimaryButton>
        ) : (
          <Stack sx={{ width: "100%", gap: "24px", padding: "25px 0px" }}>
            <Stack sx={{ width: "100%", gap: "12px" }}>
              <Stack direction="row" alignItems="flex-start">
                <AuthCheckbox
                  checked={hasAgreedTnc}
                  onChange={(e) => handleAgreementClick("tnc", e)}
                />
                <Text sx={agreementTextSx}>
                  I agree to the{" "}
                  <a
                    href={`${config.disclaimerUrl}`}
                    target="_blank"
                    rel="noreferrer"
                    style={linkSx}
                  >
                    JARVIS Disclaimer
                  </a>{" "}
                  and{" "}
                  <a
                    href={`${config.websiteUrl}/terms-and-conditions`}
                    target="_blank"
                    rel="noreferrer"
                    style={linkSx}
                  >
                    T&Cs.
                  </a>
                </Text>
              </Stack>
              <Stack direction="row" alignItems="flex-start">
                <AuthCheckbox
                  checked={hasAgreedConcent}
                  onChange={(e) => handleAgreementClick("consent", e)}
                />
                <Text sx={agreementTextSx}>
                  I give consent for communication over Call, WhatsApp, SMS, and
                  Email
                </Text>
              </Stack>
            </Stack>

            <Stack sx={{ width: "100%", gap: "12px", alignItems: "center" }}>
              <AuthPrimaryButton
                onClick={() => onRequestOtp(product)}
                loading={isLoading}
                disabled={disabledToProceed()}
                product={product}
                showArrow
              >
                Signup
              </AuthPrimaryButton>

              <Text
                sx={{
                  textAlign: "center",
                  fontSize: "14px",
                  color: "#000",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Already have an account?{" "}
                <Link
                  href={loginUrl || "/login"}
                  sx={{
                    color: "#2977ec",
                    fontWeight: 500,
                    textDecoration: "underline",
                    fontSize: "14px",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  Login Now
                </Link>
              </Text>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default SignUpForm;
